// Scored Fatigue dashboard: page chrome (title, screen nav, drill-scope switcher,
// breadcrumb) plus the active screen. Ports the prototype's sidebar/topbar to a
// horizontal nav so it sits inside the existing admin layout.

import React from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Activity,
  ChevronRight,
  CircleCheck,
  Clock3,
  Gauge,
  LayoutGrid,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Truck,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { GROUPS, REGIONS, WORKERS } from './engine/data';
import { DashProvider, useDash } from './state';
import type { DrillScope, ScreenKey } from './state';
import { OverviewScreen } from './screens/OverviewScreen';
import { PastScreen } from './screens/PastScreen';
import { TodayScreen } from './screens/TodayScreen';
import { ForwardScreen } from './screens/ForwardScreen';
import { ShiftsScreen } from './screens/ShiftsScreen';
import { DrivingScreen } from './screens/DrivingScreen';
import { ResilienceScreen } from './screens/ResilienceScreen';
import { ComplianceScreen } from './screens/ComplianceScreen';
import { RecommendationsScreen } from './screens/RecommendationsScreen';
import { WorkerScreen } from './screens/WorkerScreen';
import '../../../../../styles/fatigue-shadcn.css';

const NAV: { key: ScreenKey; label: string; icon: React.ElementType }[] = [
  { key: 'overview', label: 'Overview', icon: LayoutGrid },
  { key: 'past', label: 'Past', icon: Clock3 },
  { key: 'today', label: 'Today', icon: Gauge },
  { key: 'forward', label: 'Forward', icon: TrendingUp },
  { key: 'shifts', label: 'Shifts & Sleep', icon: Activity },
  { key: 'driving', label: 'Driving', icon: Truck },
  { key: 'resilience', label: 'OT Resilience', icon: Activity },
  { key: 'compliance', label: 'HSE Compliance', icon: ShieldCheck },
  { key: 'recommendations', label: 'AI Recommendations', icon: Sparkles },
];

const SCOPES: { key: DrillScope; label: string }[] = [
  { key: 'company', label: 'Company' },
  { key: 'region', label: 'Region' },
  { key: 'group', label: 'Group' },
  { key: 'individual', label: 'Individual' },
];

const SCREENS: Record<ScreenKey, React.FC> = {
  overview: OverviewScreen,
  past: PastScreen,
  today: TodayScreen,
  forward: ForwardScreen,
  shifts: ShiftsScreen,
  driving: DrivingScreen,
  resilience: ResilienceScreen,
  compliance: ComplianceScreen,
  recommendations: RecommendationsScreen,
  worker: WorkerScreen,
};

const Breadcrumb: React.FC = () => {
  const d = useDash();
  const crumbs: { label: string; onClick?: () => void }[] = [{ label: 'Company', onClick: d.resetToCompany }];
  if (d.activeRegion) {
    crumbs.push({ label: REGIONS[d.activeRegion].name, onClick: () => d.setDrillScope('region') });
  }
  if (d.activeGroup !== null) {
    crumbs.push({ label: GROUPS[d.activeGroup].name, onClick: () => d.setDrillScope('group') });
  }
  if (d.activeWorker && (d.drillScope === 'individual' || d.screen === 'worker')) {
    const name = WORKERS[d.activeWorker].name;
    crumbs.push({ label: name, onClick: d.screen === 'worker' ? undefined : () => d.go('worker') });
  }
  return (
    <nav className='flex flex-wrap items-center gap-1.5 text-[13px] text-muted-foreground' aria-label='Scope'>
      {crumbs.map((c, i) => (
        <React.Fragment key={`${c.label}-${i}`}>
          {i > 0 && <ChevronRight className='h-3.5 w-3.5' />}
          {c.onClick ? (
            <button type='button' onClick={c.onClick} className='text-info hover:underline'>
              {c.label}
            </button>
          ) : (
            <span className='text-foreground'>{c.label}</span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
};

const DashboardBody: React.FC = () => {
  const d = useDash();
  const Screen = SCREENS[d.screen];
  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-wrap items-start justify-between gap-4'>
        <div className='min-w-0'>
          <h1 className='text-xl font-semibold'>Fatigue Management</h1>
          <p className='mt-1 text-sm text-muted-foreground'>
            Fatigue exposure, cognitive load and operational readiness — past, today and forward.
          </p>
        </div>
        <div className='flex gap-0.5 rounded-lg bg-muted p-[3px]' role='tablist' aria-label='Drill scope'>
          {SCOPES.map((s) => (
            <button
              key={s.key}
              type='button'
              role='tab'
              aria-selected={d.drillScope === s.key}
              onClick={() => d.setDrillScope(s.key)}
              className={cn(
                'rounded-md px-3 py-1 text-xs font-medium transition-colors',
                d.drillScope === s.key
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className='-mx-1 overflow-x-auto px-1'>
        <div className='flex min-w-max gap-1 border-b border-border'>
          {NAV.map(({ key, label, icon: Icon }) => {
            const active = d.screen === key || (d.screen === 'worker' && key === 'overview');
            return (
              <button
                key={key}
                type='button'
                onClick={() => d.go(key)}
                className={cn(
                  '-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-[13px] font-medium transition-colors',
                  active
                    ? 'border-success text-success'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                <Icon className='h-3.5 w-3.5' />
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <div className='flex items-center justify-between gap-3'>
        <Breadcrumb />
        <span className='inline-flex items-center gap-1 rounded-full border border-warning/30 bg-warning/10 px-2 py-0.5 text-[11px] font-semibold text-warning'>
          <CircleCheck className='h-3 w-3' /> Demo data
        </span>
      </div>

      <Screen />
    </div>
  );
};

export const FatigueDashboard: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  return (
    <div className={cn('fatigue-page p-1', isDark && 'dark')}>
      <DashProvider>
        <DashboardBody />
      </DashProvider>
    </div>
  );
};
